import { useContext, useEffect, useState } from 'react';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import axios from 'axios';
import UserContext from '../UserContext';
import DoctorImg from '../assets/images/doctor.png';
import PatientImg from '../assets/images/patient.png';

export default function Login() {
  const [userData, setUserData] = useContext(UserContext);
  const navigate = useNavigate();
  
  // State management
  const [formData, setFormData] = useState({
    userType: '',
    email: '',
    password: ''
  });
  
  const [errors, setErrors] = useState({
    userType: '',
    email: '',
    password: ''
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [hasToken, setHasToken] = useState(false);

  // Input validation handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setApiError('');
    
    // Real-time validation
    if (name === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!value) {
        setErrors({...errors, email: 'Email is required'});
      } else if (!emailRegex.test(value)) {
        setErrors({...errors, email: 'Please enter a valid email'});
      } else {
        setErrors({...errors, email: ''});
      }
    }
    
    if (name === 'password') {
      if (!value) {
        setErrors({...errors, password: 'Password is required'});
      } else if (value.length < 4) {
        setErrors({...errors, password: 'Password must be at least 4 characters'});
      } else {
        setErrors({...errors, password: ''});
      }
    }
    
    if (name === 'userType') {
      if (!value) {
        setErrors({...errors, userType: 'Please select a user type'});
      } else {
        setErrors({...errors, userType: ''});
      }
    }
  };

  // Check if form is valid
  const isFormValid = () => {
    return (
      formData.userType &&
      formData.email &&
      formData.password &&
      !errors.userType &&
      !errors.email &&
      !errors.password
    );
  };

  // Fetch user profile after login
  const getUserDetails = async () => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${Cookies.get('userToken')}`
        }
      };
      
      const response = await axios.get('https://api.escuelajs.co/api/v1/auth/profile', config);
      setUserData(response.data);
      Cookies.set('userData', JSON.stringify(response.data), { secure: true });
      
      if (response.data.role === 'admin') {
        navigate('/doctor-dashboard');
      } else {
        navigate('/patient-dashboard');
      }
    } catch (err) {
      console.error('Profile fetch error:', err);
      Cookies.remove('userToken');
      setApiError('Session expired. Please login again.');
    }
  };

  // Handle login submission
  const handleLogin = async (e) => {
    e.preventDefault();
    setApiError('');
    
    if (!isFormValid()) return;
    
    setIsLoading(true);
    
    try {
      const response = await axios.post(
        'https://api.escuelajs.co/api/v1/auth/login',
        {
          email: formData.email,
          password: formData.password
        }
      );
      
      Cookies.set('userToken', response.data.access_token, { secure: true });
      await getUserDetails();
      
    } catch (err) {
      console.error('Login error:', err);
      setApiError(
        err.response?.status === 401 
          ? 'Invalid credentials. Please try again.'
          : err.response?.data?.message || 'Login failed. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Check for existing token on mount
  useEffect(() => {
    if (Cookies.get('userToken')) {
      setHasToken(true);
      const userData = Cookies.get('userData');
      
      if (userData) {
        const { role } = JSON.parse(userData);
        navigate(role === 'admin' ? '/doctor-dashboard' : '/patient-dashboard');
      } else {
        getUserDetails();
      }
    }
  }, []);

  return (
    <>
      {hasToken ? (
        <div className="d-flex justify-content-center align-items-center vh-100">
          <Spinner animation="border" variant="primary" />
          <span className="ms-3">Redirecting to dashboard...</span>
        </div>
      ) : (
        <div className="login-wrap">
          <div className="login-wrap-left pt-4">
            <h1>Welcome to Doctor Assistant</h1>
          </div>
          
          <div className="login-wrap-right d-flex align-items-center justify-content-center p-3 flex-column">
            <h1>Welcome Back!</h1>
            
            <div className="form-wrapper">
              {apiError && <Alert variant="danger">{apiError}</Alert>}
              
              <form onSubmit={handleLogin}>
                <div className="mb-3">
                  <Form.Label>Choose User Type</Form.Label>
                  <div>
                    <label className="custom-radio-btn">
                      <input
                        type="radio"
                        name="userType"
                        value="doctor"
                        checked={formData.userType === 'doctor'}
                        onChange={handleChange}
                      />
                      <span className="checkMark">
                        <img src={DoctorImg} alt="Doctor" />
                        Doctor
                      </span>
                    </label>
                    <label className="custom-radio-btn ms-3">
                      <input
                        type="radio"
                        name="userType"
                        value="patient"
                        checked={formData.userType === 'patient'}
                        onChange={handleChange}
                      />
                      <span className="checkMark">
                        <img src={PatientImg} alt="Patient" />
                        Patient
                      </span>
                    </label>
                  </div>
                  {errors.userType && (
                    <div className="text-danger small">{errors.userType}</div>
                  )}
                </div>

                <div className="mb-3">
                  <Form.Label htmlFor="email">Email</Form.Label>
                  <Form.Control
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    isInvalid={!!errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </div>

                <div className="mb-3">
                  <Form.Label htmlFor="password">Password</Form.Label>
                  <Form.Control
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    isInvalid={!!errors.password}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.password}
                  </Form.Control.Feedback>
                </div>

                <div className="text-center mt-5 mb-5">
                  <Button
                    variant="success"
                    className="login-btn"
                    type="submit"
                    disabled={!isFormValid() || isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Spinner
                          animation="border"
                          variant="light"
                          size="sm"
                          className="me-2"
                        />
                        Logging in...
                      </>
                    ) : (
                      'Login'
                    )}
                  </Button>
                </div>
              </form>

              <p className="text-center small">
                Don't have an account? <Link to="/signup">Sign up</Link> here
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}